import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  CheckCircle, Search, Filter, Eye, Send, RefreshCw, 
  Building2, Calendar, FileText, User, MapPin, CreditCard,
  Scale, Globe, ChevronDown, ChevronUp, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CHECK_TYPE_LABELS = {
  'address_check': 'Address Check',
  'id_check': 'ID Verification',
  'credit_check': 'Credit Check',
  'criminal_court_check': 'Criminal Court Check',
  'court_advocate_check': 'Court Advocate Check',
  'world_criminal_db': 'World Criminal DB',
  'india_criminal_db': 'India Criminal DB',
  'employment_check': 'Employment Check',
  'education_check': 'Education Check',
  'drug_test': 'Drug Test',
  'reference_check': 'Reference Check',
  'personal_reference': 'Personal Reference',
  'professional_reference': 'Professional Reference'
};

const CHECK_TYPE_ICONS = {
  'address_check': MapPin,
  'id_check': CreditCard,
  'credit_check': CreditCard,
  'criminal_court_check': Scale,
  'court_advocate_check': Scale,
  'world_criminal_db': Globe,
  'india_criminal_db': Globe
};

const STATUS_COLORS = {
  'pending': 'bg-yellow-100 text-yellow-800',
  'approved': 'bg-green-100 text-green-800',
  'vendor_assigned': 'bg-blue-100 text-blue-800',
  'in_progress': 'bg-purple-100 text-purple-800',
  'completed': 'bg-emerald-100 text-emerald-800',
  'clear': 'bg-emerald-100 text-emerald-800'
};

export default function QuickApproveAssign({ clients = [] }) {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [vendorMapping, setVendorMapping] = useState({});
  const [vendors, setVendors] = useState([]);
  const [entities, setEntities] = useState([]);
  
  // Filters
  const [checkTypeFilter, setCheckTypeFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [dateFilter, setDateFilter] = useState('latest'); // New: dropdown-based date filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // View document dialog
  const [viewingDocs, setViewingDocs] = useState(null);
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState({});
  
  // Assign dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [autoAssign, setAutoAssign] = useState(true);
  const [manualVendorId, setManualVendorId] = useState('');
  const [assigning, setAssigning] = useState(false);
  
  // Pagination
  const [qaCurrentPage, setQaCurrentPage] = useState(1);
  const [qaItemsPerPage, setQaItemsPerPage] = useState(20);
  const qaTotalPages = Math.ceil(checks.length / qaItemsPerPage);
  const qaStartIndex = (qaCurrentPage - 1) * qaItemsPerPage;
  const qaEndIndex = qaStartIndex + qaItemsPerPage;
  const paginatedChecks = checks.slice(qaStartIndex, qaEndIndex);

  useEffect(() => {
    loadVendorMapping();
    loadEntities();
  }, []);

  // Auto-reload when any filter changes
  useEffect(() => {
    loadData();
    setQaCurrentPage(1);
  }, [checkTypeFilter, clientFilter, entityFilter, statusFilter, dateFilter, dateFrom, dateTo]);

  const clearAllFilters = () => {
    setCheckTypeFilter('all');
    setClientFilter('all');
    setEntityFilter('all');
    setStatusFilter('pending');
    setDateFilter('latest');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const hasActiveFilters = checkTypeFilter !== 'all' || clientFilter !== 'all' || entityFilter !== 'all' || statusFilter !== 'pending' || dateFilter !== 'latest' || searchTerm !== '';

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (checkTypeFilter !== 'all') params.append('check_type', checkTypeFilter);
      if (clientFilter !== 'all') params.append('client_id', clientFilter);
      if (entityFilter !== 'all') params.append('entity_id', entityFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      // Handle date filter - calculate date range based on selection
      if (dateFilter === 'custom') {
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
      } else if (dateFilter !== 'latest') {
        const today = new Date();
        let fromDate = new Date();
        
        if (dateFilter === 'today') {
          fromDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        } else if (dateFilter === 'week') {
          fromDate.setDate(today.getDate() - 7);
        } else if (dateFilter === 'month') {
          fromDate.setMonth(today.getMonth() - 1);
        }
        
        params.append('date_from', fromDate.toISOString().split('T')[0]);
        params.append('date_to', today.toISOString().split('T')[0]);
      }
      
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`/quick-approve/checks?${params.toString()}`);
      setChecks(response.data || []);
    } catch (error) {
      toast.error('Failed to load checks');
    } finally {
      setLoading(false);
    }
  };

  const loadVendorMapping = async () => {
    try {
      const response = await axios.get('/quick-approve/vendor-mapping');
      setVendorMapping(response.data.mapping || {});
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.log('Failed to load vendor mapping');
    }
  };

  const loadEntities = async () => {
    try {
      const response = await axios.get('/entities');
      setEntities(response.data || []);
    } catch (error) {
      console.log('Failed to load entities');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedChecks(checks.map(c => ({ case_id: c.case_id, check_id: c.check_id })));
    } else {
      setSelectedChecks([]);
    }
  };

  const handleSelectCheck = (check, checked) => {
    if (checked) {
      setSelectedChecks([...selectedChecks, { case_id: check.case_id, check_id: check.check_id }]);
    } else {
      setSelectedChecks(selectedChecks.filter(s => !(s.case_id === check.case_id && s.check_id === check.check_id)));
    }
  };

  const isSelected = (check) => {
    return selectedChecks.some(s => s.case_id === check.case_id && s.check_id === check.check_id);
  };

  const toggleRowExpand = (checkId) => {
    setExpandedRows(prev => ({ ...prev, [checkId]: !prev[checkId] }));
  };

  const handleBulkApproveAssign = async () => {
    if (selectedChecks.length === 0) {
      toast.error('Please select at least one check');
      return;
    }
    
    setAssigning(true);
    try {
      const response = await axios.post('/quick-approve/bulk-approve-assign', {
        check_ids: selectedChecks,
        auto_assign: autoAssign,
        vendor_id: !autoAssign ? manualVendorId : null
      });
      
      toast.success(response.data.message);
      setSelectedChecks([]);
      setShowAssignDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to approve and assign checks');
    } finally {
      setAssigning(false);
    }
  };

  const getVendorForCheckType = (checkType, subType) => {
    // Map sub-types to base check types for vendor lookup
    const getBaseCheckType = (ct, st) => {
      if (st === 'address_present' || st === 'address_permanent') return 'address_check';
      if (st === 'id_pan' || st === 'id_aadhar') return 'id_check';
      if (st?.startsWith('employment_')) return 'employment_check';
      if (st?.startsWith('education_')) return 'education_check';
      return ct;
    };
    
    const baseType = getBaseCheckType(checkType, subType);
    const vendors = vendorMapping[baseType];
    if (vendors && vendors.length > 0) {
      return vendors[0].vendor_name;
    }
    return 'No vendor mapped';
  };

  const renderCheckData = (check) => {
    const data = check.check_data || {};
    const subType = check.sub_type || check.check_type;
    
    // Render based on sub_type for more specific display
    if (subType === 'address_present' || subType === 'address_permanent') {
      // Use complete_address if available, otherwise show individual fields
      if (data.complete_address || data.full_address) {
        return (
          <div className="text-xs space-y-1">
            <p><span className="font-medium">Full Address:</span> {data.complete_address || data.full_address || 'N/A'}</p>
            <p><span className="font-medium">City:</span> {data.city || 'N/A'}, {data.state || 'N/A'} - {data.pincode || 'N/A'}</p>
          </div>
        );
      }
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">House No:</span> {data.house_no || 'N/A'}</p>
          <p><span className="font-medium">Area:</span> {data.area || 'N/A'}</p>
          <p><span className="font-medium">City:</span> {data.city || 'N/A'}, {data.state || 'N/A'}</p>
          <p><span className="font-medium">Pincode:</span> {data.pincode || 'N/A'}</p>
        </div>
      );
    }
    
    if (subType === 'id_pan') {
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">PAN:</span> {data.pan_number || 'N/A'}</p>
          <p><span className="font-medium">Name:</span> {data.name || 'N/A'}</p>
        </div>
      );
    }
    
    if (subType === 'id_aadhar') {
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">Aadhar:</span> {data.aadhar_number || 'N/A'}</p>
          <p><span className="font-medium">Name:</span> {data.name || 'N/A'}</p>
        </div>
      );
    }
    
    if (subType === 'credit_check') {
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">Name:</span> {data.name || 'N/A'}</p>
          <p><span className="font-medium">PAN:</span> {data.pan_number || 'N/A'}</p>
          <p><span className="font-medium">DOB:</span> {data.dob || 'N/A'}</p>
        </div>
      );
    }
    
    if (subType === 'criminal_court_check' || subType === 'court_advocate_check' || 
        subType === 'criminal_court_present' || subType === 'criminal_court_permanent' ||
        subType === 'court_advocate_present' || subType === 'court_advocate_permanent') {
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">Name:</span> {data.name || 'N/A'}</p>
          <p><span className="font-medium">Father:</span> {data.father_name || 'N/A'}</p>
          <p><span className="font-medium">DOB:</span> {data.dob || 'N/A'}</p>
          {data.full_address ? (
            <p><span className="font-medium">Full Address:</span> {data.full_address}</p>
          ) : (
            <>
              <p><span className="font-medium">House No:</span> {data.house_no || 'N/A'}</p>
              <p><span className="font-medium">Area:</span> {data.area || 'N/A'}</p>
              <p><span className="font-medium">Landmark:</span> {data.landmark || 'N/A'}</p>
              <p><span className="font-medium">City:</span> {data.city || 'N/A'}</p>
              <p><span className="font-medium">District:</span> {data.district || 'N/A'}</p>
              <p><span className="font-medium">State:</span> {data.state || 'N/A'}</p>
              <p><span className="font-medium">Pincode:</span> {data.pincode || 'N/A'}</p>
            </>
          )}
        </div>
      );
    }
    
    if (subType === 'world_criminal_db') {
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">Name:</span> {data.name || 'N/A'}</p>
          <p><span className="font-medium">DOB:</span> {data.dob || 'N/A'}</p>
          <p><span className="font-medium">Passport:</span> {data.passport_number || 'N/A'}</p>
        </div>
      );
    }
    
    if (subType === 'india_criminal_db') {
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">Name:</span> {data.name || 'N/A'}</p>
          <p><span className="font-medium">Father:</span> {data.father_name || 'N/A'}</p>
          <p><span className="font-medium">DOB:</span> {data.dob || 'N/A'}</p>
          <p><span className="font-medium">PAN:</span> {data.pan_number || 'N/A'}</p>
        </div>
      );
    }
    
    // Employment entries
    if (subType?.startsWith('employment_')) {
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">Company:</span> {data.company_name || 'N/A'}</p>
          <p><span className="font-medium">Designation:</span> {data.designation || 'N/A'}</p>
          <p><span className="font-medium">Period:</span> {data.from_date || 'N/A'} - {data.to_date || 'Present'}</p>
          <p><span className="font-medium">Emp ID:</span> {data.employee_id || 'N/A'}</p>
        </div>
      );
    }
    
    // Education entries
    if (subType?.startsWith('education_')) {
      return (
        <div className="text-xs space-y-1">
          <p><span className="font-medium">Course:</span> {data.course || 'N/A'}</p>
          <p><span className="font-medium">University:</span> {data.university || 'N/A'}</p>
          <p><span className="font-medium">College:</span> {data.college || 'N/A'}</p>
          <p><span className="font-medium">Year:</span> {data.year_of_passing || 'N/A'}</p>
          <p><span className="font-medium">Roll No:</span> {data.roll_number || 'N/A'}</p>
        </div>
      );
    }
    
    // Default - show all data keys
    return (
      <div className="text-xs space-y-1">
        {Object.entries(data).slice(0, 4).map(([key, value]) => (
          <p key={key}>
            <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {String(value) || 'N/A'}
          </p>
        ))}
      </div>
    );
  };

  const CheckIcon = ({ checkType }) => {
    const Icon = CHECK_TYPE_ICONS[checkType] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Quick Approve & Assign
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
            {/* Check Type Filter */}
            <div>
              <Label className="text-xs">Check Type</Label>
              <Select value={checkTypeFilter} onValueChange={setCheckTypeFilter}>
                <SelectTrigger className="h-9" data-testid="qa-check-type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="address_present">Address - Present</SelectItem>
                  <SelectItem value="address_permanent">Address - Permanent</SelectItem>
                  <SelectItem value="id_pan">ID - PAN Card</SelectItem>
                  <SelectItem value="id_aadhar">ID - Aadhar Card</SelectItem>
                  <SelectItem value="credit_check">Credit Check</SelectItem>
                  <SelectItem value="criminal_court_check">Criminal Court Check</SelectItem>
                  <SelectItem value="criminal_advocate_check">Criminal Advocate Check</SelectItem>
                  <SelectItem value="employment_check">Employment Check</SelectItem>
                  <SelectItem value="education_check">Education Check</SelectItem>
                  <SelectItem value="reference_check">Reference Check</SelectItem>
                  <SelectItem value="professional_reference">Professional Reference</SelectItem>
                  <SelectItem value="drug_test">Drug Test</SelectItem>
                  <SelectItem value="global_database">Global Database</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Client Filter */}
            <div>
              <Label className="text-xs">Client</Label>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="h-9" data-testid="qa-client-filter">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Entity Filter */}
            <div>
              <Label className="text-xs">Entity</Label>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="h-9" data-testid="qa-entity-filter">
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entities.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.entity_name || e.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9" data-testid="qa-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="vendor_assigned">Vendor Assigned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Filter Dropdown */}
            <div>
              <Label className="text-xs">Date</Label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="h-9" data-testid="qa-date-filter">
                  <SelectValue placeholder="Latest" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest (All)</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom Date Range - only show when custom is selected */}
            {dateFilter === 'custom' && (
              <>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input 
                    type="date" 
                    value={dateFrom} 
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input 
                    type="date" 
                    value={dateTo} 
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9"
                  />
                </div>
              </>
            )}
            
            {/* Search */}
            <div>
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input 
                  placeholder="Name, Case ID, Entity..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadData()}
                  className="h-9 pl-8"
                />
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-between items-center mt-4 pt-3 border-t">
            <div className="flex gap-2">
              <Button onClick={loadData} variant="outline" size="sm" disabled={loading} data-testid="qa-search-btn">
                <Search className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Searching...' : 'Search'}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearAllFilters} variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700" data-testid="qa-clear-filters-btn">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Clear Filters
                </Button>
              )}
              <span className="text-sm text-slate-500 self-center">
                {checks.length} checks found | {selectedChecks.length} selected
              </span>
            </div>
            
            <Button 
              onClick={() => setShowAssignDialog(true)}
              disabled={selectedChecks.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Approve & Assign ({selectedChecks.length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Checks Table */}
      <Card>
        {/* Pagination Controls - Above Table */}
        {checks.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50" data-testid="qa-pagination-top">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Showing {qaStartIndex + 1} to {Math.min(qaEndIndex, checks.length)} of {checks.length} checks
              </span>
              <Select value={qaItemsPerPage.toString()} onValueChange={(val) => { setQaItemsPerPage(parseInt(val)); setQaCurrentPage(1); }}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 / page</SelectItem>
                  <SelectItem value="20">20 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setQaCurrentPage(1)} disabled={qaCurrentPage === 1}>First</Button>
              <Button variant="outline" size="sm" onClick={() => setQaCurrentPage(p => Math.max(1, p - 1))} disabled={qaCurrentPage === 1}>Previous</Button>
              <span className="px-3 py-1 text-sm font-medium">Page {qaCurrentPage} of {qaTotalPages || 1}</span>
              <Button variant="outline" size="sm" onClick={() => setQaCurrentPage(p => Math.min(qaTotalPages, p + 1))} disabled={qaCurrentPage >= qaTotalPages}>Next</Button>
              <Button variant="outline" size="sm" onClick={() => setQaCurrentPage(qaTotalPages)} disabled={qaCurrentPage >= qaTotalPages}>Last</Button>
            </div>
          </div>
        )}
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={selectedChecks.length === paginatedChecks.length && paginatedChecks.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="text-xs">Case ID</TableHead>
                  <TableHead className="text-xs">Employee</TableHead>
                  <TableHead className="text-xs">Check Type</TableHead>
                  <TableHead className="text-xs">Check Data</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Client / Entity</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Vendor</TableHead>
                  <TableHead className="text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedChecks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                      No checks found matching the criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedChecks.map((check, index) => (
                    <React.Fragment key={`${check.case_id}-${check.check_id}-${check.sub_type || index}`}>
                      <TableRow 
                        className={`hover:bg-slate-50 ${isSelected(check) ? 'bg-emerald-50' : ''}`}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={isSelected(check)}
                            onCheckedChange={(checked) => handleSelectCheck(check, checked)}
                          />
                        </TableCell>
                        <TableCell className="text-xs font-mono">{check.case_id}</TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <p className="font-medium">{check.employee_name}</p>
                            <p className="text-slate-500">{check.father_name && `S/o ${check.father_name}`}</p>
                            {check.contact_number && <p className="text-slate-400">{check.contact_number}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            <CheckIcon checkType={typeof check.check_type === 'string' ? check.check_type : 'custom_check'} />
                            <span className="ml-1">
                              {check.display_name || (typeof check.check_type === 'string' 
                                ? (CHECK_TYPE_LABELS[check.check_type] || check.check_type.replace(/_/g, ' '))
                                : 'Custom Check')
                              }
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          {renderCheckData(check)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${STATUS_COLORS[check.status] || 'bg-slate-100'}`}>
                            {check.status}
                          </Badge>
                          {check.has_insufficiency && (
                            <Badge className="ml-1 bg-rose-100 text-rose-700 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />Insuff
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>
                            <p className="font-medium">{check.client_name}</p>
                            {check.entity_name && <p className="text-slate-500">{check.entity_name}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          {check.created_at ? new Date(check.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {check.assigned_vendor?.vendor_name || (
                            <span className="text-slate-400 italic">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => toggleRowExpand(check.check_id)}
                              className="h-7 w-7 p-0"
                            >
                              {expandedRows[check.check_id] ? 
                                <ChevronUp className="w-4 h-4" /> : 
                                <ChevronDown className="w-4 h-4" />
                              }
                            </Button>
                            {check.documents?.length > 0 && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setViewingDocs(check)}
                                className="h-7 w-7 p-0"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Row */}
                      {expandedRows[check.check_id] && (
                        <TableRow className="bg-slate-50">
                          <TableCell colSpan={10}>
                            <div className="p-3 grid grid-cols-4 gap-4 text-xs">
                              <div>
                                <p className="font-medium text-slate-600">Check ID</p>
                                <p className="font-mono">{check.check_id}</p>
                              </div>
                              <div>
                                <p className="font-medium text-slate-600">Employee ID</p>
                                <p className="font-mono">{check.employee_id}</p>
                              </div>
                              <div>
                                <p className="font-medium text-slate-600">DOB</p>
                                <p>{check.dob || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="font-medium text-slate-600">Documents</p>
                                <p>{check.documents?.length || 0} attached</p>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Approve & Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve & Assign Checks</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              You are about to approve and assign <strong>{selectedChecks.length}</strong> checks.
            </p>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="auto-assign" 
                checked={autoAssign}
                onCheckedChange={setAutoAssign}
              />
              <Label htmlFor="auto-assign" className="text-sm">
                Auto-assign to vendor based on check type mapping
              </Label>
            </div>
            
            {!autoAssign && (
              <div>
                <Label className="text-sm">Select Vendor</Label>
                <Select value={manualVendorId} onValueChange={setManualVendorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} ({v.specializations?.join(', ') || 'No specialization'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="bg-slate-50 p-3 rounded text-xs">
              <p className="font-medium mb-2">Assignment Preview:</p>
              {Object.keys(vendorMapping).slice(0, 8).map(ct => (
                <div key={ct} className="flex justify-between">
                  <span>{CHECK_TYPE_LABELS[ct] || ct}</span>
                  <span className="text-slate-600">{vendorMapping[ct]?.[0]?.agent_name || vendorMapping[ct]?.[0]?.vendor_name || 'None'}</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkApproveAssign}
              disabled={assigning || (!autoAssign && !manualVendorId)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {assigning ? 'Processing...' : 'Approve & Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Documents Dialog */}
      <Dialog open={!!viewingDocs} onOpenChange={() => setViewingDocs(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Documents - {viewingDocs?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {viewingDocs?.documents?.length > 0 ? (
              viewingDocs.documents.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">{doc.filename || doc.doc_type || `Document ${idx + 1}`}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(doc.file_url?.startsWith('http') ? doc.file_url : `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`, '_blank')}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 py-4">No documents attached</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
